import { useMemo, useState, type ReactElement } from "react";

type Lang = "sv" | "en";
type EntryKey = "horn" | "klara" | "drott";

const S = 5.6, OX = 215, AX = 1, AY = 0.26, BX = -0.55, BY = 0.52, STEP = 162;
const VB_W = 960, VB_H = 1070;

const FLOORS = [
  { id: 0, oy: 755 },
  { id: 1, oy: 755 - STEP },
  { id: 2, oy: 755 - STEP * 2 },
  { id: 3, oy: 755 - STEP * 3 },
];

const UNITS: [number, number, number, number][] = [
  [6, 2, 26, 12], [26, 2, 48, 12], [48, 2, 70, 12], [70, 2, 92, 12], [92, 2, 114, 12],
  [24, 19, 46, 29], [46, 19, 68, 29], [68, 19, 90, 29], [90, 19, 114, 29],
];

const SALON: [number, number, number, number] = [7, 21, 21, 29];

const SHOPS: [number, number, number, number, string][] = [
  [22, 19, 25.6, 29, "Replay"], [25.6, 19, 29.2, 29, "Diesel"], [29.2, 19, 32.8, 29, "Lee"],
  [32.8, 19, 36.4, 29, "Levi's"], [36.4, 19, 40, 29, "Nudie"], [43, 19, 49, 29, "Kassa"],
  [49, 19, 66, 29, ""], [66, 19, 84, 29, ""], [84, 19, 100, 29, ""], [100, 19, 114, 29, ""],
];

const STACKS = {
  w: { name: "Rulltrappa 2", x0: 24, x1: 40, y0: 12.6, y1: 17.4, dirs: [1, -1, 1] },
  e: { name: "Rulltrappa 1", x0: 68, x1: 84, y0: 12.6, y1: 17.4, dirs: [-1, 1, -1] },
} as const;

type StackKey = keyof typeof STACKS;

const ENTRIES: Record<EntryKey, { at: [number, number]; stack: StackKey; min: number; walk: [number, number][] }> = {
  horn: { at: [8, 29.5], stack: "w", min: 2, walk: [[8, 29.5], [13, 24], [18, 17], [24, 15.2]] },
  klara: { at: [62, 29.5], stack: "w", min: 3, walk: [[62, 29.5], [56, 22], [40, 16], [24, 15.2]] },
  drott: { at: [119, 15], stack: "e", min: 5, walk: [[119, 15], [104, 15.2], [90, 15.2], [84, 15.2]] },
};

const COPY = {
  sv: {
    title: "Hitta till salongen",
    ask: "Vilken entré går du in genom?",
    swipe: "Svep i sidled för att se hela kartan",
    floors: ["Gatuplan", "Plan 1", "Plan 2", "Plan 3"],
    entries: { horn: "Hörnentrén", klara: "Klarabergsgatan", drott: "Drottninggatan" },
    desc: {
      horn: "kortaste vägen till salongen",
      klara: "via rulltrapporna mitt i varuhuset",
      drott: "längsta vägen, du går genom hela plan 3",
    },
    minutes: (n: number) => `Ca ${n} min`,
    first: {
      horn: "Gå in genom hörnentrén, i hörnet Klarabergsgatan och Klara Norra Kyrkogata.",
      klara: "Gå in genom entrén på Klarabergsgatan, mitt på långsidan.",
      drott: "Gå in genom entrén mot Drottninggatan.",
    },
    escNear: "Ta rulltrappan direkt innanför entrén, Rulltrappa 1.",
    escMid: "Gå till rulltrapporna mitt i varuhuset, Rulltrappa 2.",
    ride: "Åk upp tre våningar. Trapporna är saxade, så nästa startar där du kliver av, åt motsatt håll.",
    walkFar: "Uppe på plan 3 går du genom hela våningen, förbi kassan och butiksraden med Nudie, Levi's, Lee, Diesel och Replay.",
    walkNear: "Uppe på plan 3 går du mot salongens ände av huset, förbi kassan och butiksraden.",
    arrive: "Direkt efter Replay ligger vi, längst in i hörnet mot Klarabergsgatan och Klara Norra Kyrkogata.",
    streets: ["KLARABERGSGATAN", "MÄSTER SAMUELSGATAN", "DROTTNINGGATAN", "KLARA NORRA KYRKOGATA"],
  },
  en: {
    title: "How to find us",
    ask: "Which entrance are you using?",
    swipe: "Swipe sideways to see the whole map",
    floors: ["Street level", "Level 1", "Level 2", "Level 3"],
    entries: { horn: "Corner entrance", klara: "Klarabergsgatan", drott: "Drottninggatan" },
    desc: {
      horn: "the shortest way to the shop",
      klara: "via the escalators in the middle",
      drott: "the longest way, across all of level 3",
    },
    minutes: (n: number) => `About ${n} min`,
    first: {
      horn: "Enter through the corner entrance, where Klarabergsgatan meets Klara Norra Kyrkogata.",
      klara: "Enter through the Klarabergsgatan entrance, halfway along the building.",
      drott: "Enter through the Drottninggatan entrance.",
    },
    escNear: "Take the escalator just inside the entrance, Escalator 1.",
    escMid: "Walk to the escalators in the middle of the store, Escalator 2.",
    ride: "Ride up three levels. The escalators are staggered, so the next one starts where you step off, facing the other way.",
    walkFar: "On level 3, walk the length of the floor, past the tills and the row of shops: Nudie, Levi's, Lee, Diesel and Replay.",
    walkNear: "On level 3, head for our end of the building, past the tills and the row of shops.",
    arrive: "We are right after Replay, in the far corner towards Klarabergsgatan and Klara Norra Kyrkogata.",
    streets: ["KLARABERGSGATAN", "MÄSTER SAMUELSGATAN", "DROTTNINGGATAN", "KLARA NORRA KYRKOGATA"],
  },
} as const;

function P(x: number, y: number, z: number, oy: number): [number, number] {
  return [OX + (x * AX + y * BX) * S, oy + (x * AY + y * BY) * S - z * S];
}
const pts = (a: [number, number][]) => a.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
const lerp = (p: [number, number], q: [number, number], t: number): [number, number] =>
  [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t];
const flat = (c: [number, number]) => `matrix(${AX},${AY},${BX},${BY},${c[0]},${c[1]})`;

function flight(st: (typeof STACKS)[StackKey], lvl: number) {
  const d = st.dirs[lvl];
  return { inX: d > 0 ? st.x0 : st.x1, outX: d > 0 ? st.x1 : st.x0, lo: FLOORS[lvl].oy, hi: FLOORS[lvl + 1].oy };
}

function Slab({ x0, y0, x1, y1, h, top, side, oy }: {
  x0: number; y0: number; x1: number; y1: number; h: number; top: string; side: string; oy: number;
}) {
  return (
    <g>
      {h > 0 && (
        <>
          <polygon points={pts([P(x0, y1, h, oy), P(x1, y1, h, oy), P(x1, y1, 0, oy), P(x0, y1, 0, oy)])} fill={side} />
          <polygon points={pts([P(x1, y0, h, oy), P(x1, y1, h, oy), P(x1, y1, 0, oy), P(x1, y0, 0, oy)])} fill={side} />
        </>
      )}
      <polygon
        points={pts([P(x0, y0, h, oy), P(x1, y0, h, oy), P(x1, y1, h, oy), P(x0, y1, h, oy)])}
        fill={top} stroke="rgba(0,0,0,.13)" strokeWidth={1}
      />
    </g>
  );
}

export default function AhlensWayfinder({ lang = "sv" }: { lang?: Lang }) {
  const [entry, setEntry] = useState<EntryKey>("horn");
  const t = COPY[lang];

  const escalators = useMemo(() => {
    const out: ReactElement[] = [];
    (Object.keys(STACKS) as StackKey[]).forEach((k) => {
      const st = STACKS[k];
      for (let lvl = 0; lvl < 3; lvl++) {
        const fl = flight(st, lvl);
        const a0 = P(fl.inX, st.y0, 1.2, fl.lo), a1 = P(fl.inX, st.y1, 1.2, fl.lo);
        const b0 = P(fl.outX, st.y0, 1.2, fl.hi), b1 = P(fl.outX, st.y1, 1.2, fl.hi);
        const id = `ramp-${k}-${lvl}`;
        const N = 9;
        const treads: ReactElement[] = [];
        for (let i = -1; i <= N; i++) {
          const s0 = lerp(a0, b0, i / N), s1 = lerp(a1, b1, i / N);
          treads.push(<line key={i} x1={s0[0]} y1={s0[1]} x2={s1[0]} y2={s1[1]} />);
        }
        const d1 = lerp(a0, b0, 1 / N);
        const m0 = lerp(a0, a1, 0.5), m1 = lerp(b0, b1, 0.5);
        const ang = (Math.atan2(m1[1] - m0[1], m1[0] - m0[0]) * 180) / Math.PI;
        out.push(
          <g key={id}>
            <polygon points={pts([a1, b1, [b1[0], b1[1] + 7], [a1[0], a1[1] + 7]])} fill="var(--wf-ramp-side)" />
            <polygon points={pts([a0, a1, b1, b0])} fill="var(--wf-ramp)" stroke="rgba(0,0,0,.18)" strokeWidth={1} />
            <clipPath id={id}><polygon points={pts([a0, a1, b1, b0])} /></clipPath>
            <g className="wf-treads" clipPath={`url(#${id})`}
               style={{ ["--dx" as string]: `${(d1[0] - a0[0]).toFixed(2)}px`, ["--dy" as string]: `${(d1[1] - a0[1]).toFixed(2)}px` }}>
              {treads}
            </g>
            <line x1={a0[0]} y1={a0[1] - 4} x2={b0[0]} y2={b0[1] - 4} className="wf-rail" />
            <line x1={a1[0]} y1={a1[1] - 4} x2={b1[0]} y2={b1[1] - 4} className="wf-rail" />
            {[0.42, 0.62, 0.82].map((tt, i) => {
              const c = lerp(m0, m1, tt);
              return <path key={i} d="M-5,-5 L2,0 L-5,5" fill="none" stroke="var(--wf-step)" strokeWidth={2.2}
                           strokeLinecap="round" strokeLinejoin="round"
                           transform={`translate(${c[0]},${c[1]}) rotate(${ang})`} />;
            })}
          </g>
        );
      }
    });
    return out;
  }, []);

  const route = useMemo(() => {
    const e = ENTRIES[entry], st = STACKS[e.stack], Z = 4.4, Y = 15.2;
    const segs: string[] = [];
    const pathOn = (points: [number, number][], oy: number) =>
      points.map((p, i) => {
        const q = P(p[0], p[1], Z, oy);
        return `${i ? "L" : "M"}${q[0].toFixed(1)} ${q[1].toFixed(1)}`;
      }).join(" ");
    segs.push(pathOn(e.walk, FLOORS[0].oy));
    const badges: { x: number; y: number; n: number }[] = [];
    for (let lvl = 0; lvl < 3; lvl++) {
      const fl = flight(st, lvl);
      if (lvl > 0) {
        const prev = flight(st, lvl - 1);
        if (Math.abs(prev.outX - fl.inX) > 0.5) segs.push(pathOn([[prev.outX, Y], [fl.inX, Y]], fl.lo));
      }
      const a = P(fl.inX, Y, Z, fl.lo), b = P(fl.outX, Y, Z, fl.hi);
      segs.push(`M${a[0].toFixed(1)} ${a[1].toFixed(1)}L${b[0].toFixed(1)} ${b[1].toFixed(1)}`);
      badges.push({ x: (a[0] + b[0]) / 2, y: (a[1] + b[1]) / 2, n: lvl + 1 });
    }
    segs.push(pathOn([[flight(st, 2).outX, Y], [13, Y], [13, 20.4]], FLOORS[3].oy));
    return { segs, badges, start: P(e.at[0], e.at[1], Z, FLOORS[0].oy), end: P(13, 20.4, Z, FLOORS[3].oy) };
  }, [entry]);

  const e = ENTRIES[entry];
  const far = e.stack === "e";
  const steps = [t.first[entry], far ? t.escNear : t.escMid, t.ride, far ? t.walkFar : t.walkNear, t.arrive];
  const named = SHOPS.filter((s) => s[4]);

  return (
    <section className="w-full">
      <style>{`
        .wf { --wf-floor:#eae7e1; --wf-walk:#fff; --wf-unit:#c3dec8; --wf-unit-side:#95bd9c;
              --wf-dim:#e2dfd9; --wf-dim-side:#cac6bf; --wf-ramp:#cfd8d2; --wf-ramp-side:#aab5ae;
              --wf-step:#6d7d73; --wf-salon:#1d1b19; --wf-salon-side:#0d0c0b;
              --wf-gold:#c8a24a; --wf-route:#c3122b; --wf-ink:#191817; --wf-muted:#6d6761; }
        .wf-treads { animation: wf-tread 1.1s linear infinite; }
        .wf-treads line { stroke: var(--wf-step); stroke-width:1.5; stroke-linecap:round; opacity:.75; }
        @keyframes wf-tread { to { transform: translate(var(--dx), var(--dy)); } }
        .wf-rail { stroke: var(--wf-step); stroke-width:2.6; stroke-linecap:round; opacity:.9; }
        .wf-pulse { animation: wf-pulse 1.9s ease-out infinite; }
        @keyframes wf-pulse { 0% { r:7; opacity:.55 } 70%,100% { r:18; opacity:0 } }
        .wf-scroll { overflow-x:auto; -webkit-overflow-scrolling:touch; }
        .wf-scroll svg { width:100%; height:auto; display:block; min-width:720px; }
        @media (prefers-reduced-motion: reduce) { .wf-treads, .wf-pulse { animation:none } }
      `}</style>

      <div className="wf">
        <p className="text-sm font-semibold mb-2">{t.ask}</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {(Object.keys(ENTRIES) as EntryKey[]).map((k) => (
            <button key={k} type="button" onClick={() => setEntry(k)} aria-pressed={entry === k}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                entry === k ? "bg-[var(--wf-route)] border-[var(--wf-route)] text-white"
                            : "bg-white border-neutral-200 text-neutral-900 hover:border-[var(--wf-route)]"}`}>
              {t.entries[k]}
            </button>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.4fr_.85fr] lg:items-start">
          <div>
            <div className="wf-scroll rounded-xl border border-neutral-200 bg-white p-2">
              <svg viewBox={`0 0 ${VB_W} ${VB_H}`} role="img" aria-label={t.title}>
                {FLOORS.map((f) => {
                  const top = f.id === 3;
                  return (
                    <g key={f.id}>
                      <Slab x0={0} y0={0} x1={120} y1={30} h={0.8} top="var(--wf-floor)" side="rgba(0,0,0,.16)" oy={f.oy} />
                      {top && <>
                        <Slab x0={2} y0={12} x1={118} y1={18} h={1} top="var(--wf-walk)" side="rgba(0,0,0,.08)" oy={f.oy} />
                        <Slab x0={7} y0={18} x1={22} y1={21} h={1} top="var(--wf-walk)" side="rgba(0,0,0,.08)" oy={f.oy} />
                      </>}
                      {UNITS.filter((u) => !(top && u[1] > 15)).map((u, i) => (
                        <Slab key={i} x0={u[0]} y0={u[1]} x1={u[2]} y1={u[3]} h={2.6} oy={f.oy}
                          top={top ? "var(--wf-unit)" : "var(--wf-dim)"} side={top ? "var(--wf-unit-side)" : "var(--wf-dim-side)"} />
                      ))}
                      {top && SHOPS.map((u, i) => (
                        <Slab key={`s${i}`} x0={u[0]} y0={u[1]} x1={u[2]} y1={u[3]} h={2.6} oy={f.oy}
                          top="var(--wf-unit)" side="var(--wf-unit-side)" />
                      ))}
                      {top && <Slab x0={SALON[0]} y0={SALON[1]} x1={SALON[2]} y1={SALON[3]} h={3.6} oy={f.oy}
                        top="var(--wf-salon)" side="var(--wf-salon-side)" />}
                      {top && named.map((u, i) => {
                        const c = P((u[0] + u[2]) / 2, 24, 2.6, f.oy);
                        return (
                          <g key={`n${i}`}>
                            <circle cx={c[0]} cy={c[1]} r={8.5} fill="#fff" stroke="var(--wf-step)" strokeWidth={1.4} />
                            <text x={c[0]} y={c[1] + 4} textAnchor="middle" fontSize={11} fontWeight={800} fill="var(--wf-ink)">{i + 1}</text>
                          </g>
                        );
                      })}
                      <text x={P(-4, 30, 0, f.oy)[0] - 6} y={P(-4, 30, 0, f.oy)[1]} textAnchor="end"
                        fontSize={13} fontWeight={top ? 800 : 600} fill={top ? "var(--wf-ink)" : "var(--wf-muted)"}>
                        {t.floors[f.id]}
                      </text>
                    </g>
                  );
                })}

                {escalators}

                {(Object.keys(STACKS) as StackKey[]).map((k) =>
                  [FLOORS[0], FLOORS[3]].map((fl) => {
                    const st = STACKS[k];
                    const c = P((st.x0 + st.x1) / 2, fl.id === 3 ? st.y0 - 3.2 : st.y1 + 3.4, 0, fl.oy);
                    return (
                      <text key={`${k}${fl.id}`} transform={flat(c)} textAnchor="middle" fontSize={9}
                        fontWeight={700} fill="var(--wf-muted)">{st.name}</text>
                    );
                  })
                )}

                {([[60, 34, 0], [60, -5, 1], [126, 20, 2], [-8, 14, 3]] as const).map((d, i) => {
                  const c = P(d[0], d[1], 0, FLOORS[0].oy);
                  return <text key={i} transform={flat(c)} textAnchor="middle" fontSize={10}
                    letterSpacing=".2em" fill="var(--wf-muted)">{t.streets[d[2]]}</text>;
                })}

                {(() => {
                  const c = P((SALON[0] + SALON[2]) / 2, (SALON[1] + SALON[3]) / 2, 3.6, FLOORS[3].oy);
                  return (
                    <g>
                      <line x1={c[0]} y1={c[1]} x2={c[0]} y2={c[1] - 42} stroke="var(--wf-gold)" strokeWidth={2.4} />
                      <circle cx={c[0]} cy={c[1] - 50} r={9} fill="var(--wf-gold)" />
                      <circle cx={c[0]} cy={c[1]} r={3} fill="var(--wf-gold)" />
                      <text x={c[0]} y={c[1] - 64} textAnchor="middle" fontSize={13} fontWeight={800} fill="var(--wf-ink)">
                        TAILOR&rsquo;S BARBER
                      </text>
                    </g>
                  );
                })()}

                <g>
                  {route.segs.map((d, i) => (
                    <g key={i}>
                      <path d={d} fill="none" stroke="rgba(0,0,0,.16)" strokeWidth={8} strokeLinecap="round" strokeLinejoin="round" />
                      <path d={d} fill="none" stroke="var(--wf-route)" strokeWidth={4.4} strokeDasharray="11 9"
                        strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                  ))}
                  <circle cx={route.start[0]} cy={route.start[1]} r={7} fill="var(--wf-route)" className="wf-pulse" />
                  <circle cx={route.start[0]} cy={route.start[1]} r={7} fill="var(--wf-route)" />
                  <text x={route.start[0]} y={route.start[1] - 15} textAnchor="middle" fontSize={12}
                    fontWeight={800} fill="var(--wf-ink)">{t.entries[entry]}</text>
                  {route.badges.map((b) => (
                    <g key={b.n}>
                      <circle cx={b.x} cy={b.y} r={11.5} fill="var(--wf-route)" />
                      <text x={b.x} y={b.y + 4} textAnchor="middle" fontSize={12} fontWeight={800} fill="#fff">{b.n}</text>
                    </g>
                  ))}
                  <circle cx={route.end[0]} cy={route.end[1]} r={10} fill="none" stroke="var(--wf-route)" strokeWidth={4} />
                </g>
              </svg>
            </div>
            <p className="mt-2 text-xs text-neutral-500 lg:hidden">{t.swipe}</p>
          </div>

          <div>
            <p className="mb-3">
              <strong className="text-lg">{t.minutes(e.min)}</strong>{" "}
              <span className="text-sm text-neutral-500">{t.desc[entry]}</span>
            </p>
            <ol className="m-0 list-none p-0">
              {steps.map((s, i) => (
                <li key={i} className="relative mb-3 pl-10">
                  <span className="absolute left-0 top-0 grid h-7 w-7 place-items-center rounded-full bg-[var(--wf-route)] text-xs font-bold text-white">
                    {i + 1}
                  </span>
                  {s}
                </li>
              ))}
            </ol>
            <div className="mt-4 flex flex-wrap gap-3 text-sm text-neutral-500">
              {named.map((u, i) => (
                <span key={i} className="inline-flex items-center gap-1.5">
                  <b className="grid h-5 w-5 place-items-center rounded-full border border-neutral-400 text-[11px] font-bold text-neutral-700">{i + 1}</b>
                  {u[4]}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
